import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import TopNav from '../../components/layout/TopNav';
import api from '../../services/api';
import GradeBadge from '../../components/shared/GradeBadge';
import ScoringStatusChip from '../../components/shared/ScoringStatusChip';
import type { Tenant, Video, Grade, ScoringStatus } from '../../types';

interface UserOption {
  id: string;
  firstName: string | null;
  lastName: string | null;
  email: string;
}

type ViewMode = 'grid' | 'list';

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses' },
  { value: 'SCORED', label: 'Scored' },
  { value: 'FLAGGED', label: 'Flagged' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'PROCESSING', label: 'Processing' },
  { value: 'FAILED', label: 'Failed' },
];

const GRADE_OPTIONS = [
  { value: '', label: 'All Grades' },
  { value: 'A', label: 'Grade A  (85+)' },
  { value: 'B', label: 'Grade B  (70–84)' },
  { value: 'C', label: 'Grade C  (55–69)' },
  { value: 'D', label: 'Grade D  (40–54)' },
  { value: 'F', label: 'Grade F  (0–39)' },
];

const SORT_OPTIONS = [
  { value: '', label: 'Newest First' },
  { value: 'oldest', label: 'Oldest First' },
  { value: 'score_desc', label: 'Highest Score' },
  { value: 'score_asc', label: 'Lowest Score' },
];

const selectClass = "h-10 px-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm font-bold text-slate-600 dark:text-slate-400 outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 transition-all cursor-pointer appearance-none pr-8 min-w-[140px]";
const selectStyle = { backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='16' height='16' viewBox='0 0 24 24' fill='none' stroke='%2394a3b8' stroke-width='2.5' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='M6 9l6 6 6-6'/%3E%3C/svg%3E")`, backgroundRepeat: 'no-repeat' as const, backgroundPosition: 'right 10px center' };
const dateClass = "h-10 px-4 rounded-xl bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-sm font-bold text-slate-600 dark:text-slate-400 outline-none focus:border-primary focus:ring-4 focus:ring-primary/5 transition-all cursor-pointer";

const GroupAdminVideosPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [selectedTenantId, setSelectedTenantId] = useState(searchParams.get('tenantId') ?? '');
  const [users, setUsers] = useState<UserOption[]>([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [selectedStatus, setSelectedStatus] = useState(searchParams.get('status') ?? '');
  const [selectedGrade, setSelectedGrade] = useState('');
  const [selectedSort, setSelectedSort] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [viewMode, setViewMode] = useState<ViewMode>('grid');
  const [videos, setVideos] = useState<Video[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [tenantsLoading, setTenantsLoading] = useState(true);
  const [thumbnailUrls, setThumbnailUrls] = useState<Record<string, string>>({});
  const pageSize = 20;

  const tenantMap = Object.fromEntries(tenants.map((t) => [t.id, t.name]));

  useEffect(() => {
    api.get('/group-admin/tenants')
      .then((res) => setTenants(res.data.data ?? []))
      .finally(() => setTenantsLoading(false));
  }, []);

  useEffect(() => {
    if (!selectedTenantId) {
      setUsers([]);
      setSelectedUserId('');
      return;
    }
    api.get(`/group-admin/tenants/${selectedTenantId}/users`)
      .then((res) => setUsers(res.data.data ?? []));
    setSelectedUserId('');
  }, [selectedTenantId]);

  const loadVideos = useCallback(async (p: number) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(p), pageSize: String(pageSize) });
      if (selectedTenantId) params.set('tenantId', selectedTenantId);
      if (selectedUserId) params.set('userId', selectedUserId);
      if (selectedStatus) params.set('status', selectedStatus);
      if (selectedGrade) params.set('grade', selectedGrade);
      if (selectedSort) params.set('sort', selectedSort);
      if (dateFrom) params.set('dateFrom', dateFrom);
      if (dateTo) params.set('dateTo', dateTo);
      const res = await api.get(`/group-admin/videos?${params.toString()}`);
      const videoList: Video[] = res.data.data ?? [];
      setVideos(videoList);
      setTotal(res.data.meta?.total ?? 0);

      const ids = videoList.map((v) => v.id);
      if (ids.length > 0) {
        try {
          const thumbRes = await api.post('/group-admin/thumbnail-urls', { videoIds: ids });
          setThumbnailUrls(thumbRes.data.data ?? {});
        } catch {
          setThumbnailUrls({});
        }
      } else {
        setThumbnailUrls({});
      }
    } finally {
      setLoading(false);
    }
  }, [selectedTenantId, selectedUserId, selectedStatus, selectedGrade, selectedSort, dateFrom, dateTo]);

  useEffect(() => {
    setPage(1);
    void loadVideos(1);
  }, [selectedTenantId, selectedUserId, selectedStatus, selectedGrade, selectedSort, dateFrom, dateTo, loadVideos]);

  const totalPages = Math.ceil(total / pageSize);
  const userName = (u: UserOption) =>
    [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email;

  const uploaderName = (video: Video): string => {
    if (!video.uploader) return '—';
    return [video.uploader.firstName, video.uploader.lastName].filter(Boolean).join(' ') || video.uploader.email;
  };

  const hasActiveFilters = selectedTenantId || selectedUserId || selectedStatus || selectedGrade || selectedSort || dateFrom || dateTo;

  const clearFilters = () => {
    setSelectedTenantId('');
    setSelectedUserId('');
    setSelectedStatus('');
    setSelectedGrade('');
    setSelectedSort('');
    setDateFrom('');
    setDateTo('');
  };

  const VideoCard: React.FC<{ video: Video }> = ({ video }) => {
    const isClickable = video.scoringStatus !== 'PENDING' && video.scoringStatus !== 'PROCESSING';
    const thumbUrl = thumbnailUrls[video.id];
    const dealershipName = tenantMap[video.tenantId] ?? '';
    const uploaderLabel = uploaderName(video);
    return (
      <div
        onClick={() => isClickable && navigate(`/videos/${video.id}`)}
        className={`bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden shadow-sm dark:shadow-none transition-all group flex flex-col h-full ${isClickable ? 'cursor-pointer hover:shadow-xl hover:shadow-primary/5 dark:hover:border-primary/30' : 'cursor-default opacity-60'}`}
      >
        <div className="aspect-video bg-slate-100 dark:bg-slate-800 relative overflow-hidden shrink-0">
          {thumbUrl ? (
            <img src={thumbUrl} alt={video.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-300 dark:text-slate-700">
              <span className="material-symbols-outlined text-5xl">videocam</span>
            </div>
          )}
          <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-all flex items-center justify-center">
            <div className="w-12 h-12 rounded-full bg-white/30 backdrop-blur-md flex items-center justify-center text-white opacity-0 group-hover:opacity-100 group-hover:scale-110 transition-all shadow-xl">
              <span className="material-symbols-outlined text-[32px]">play_arrow</span>
            </div>
          </div>
          {video.durationSeconds && (
            <div className="absolute bottom-3 right-3 px-2 py-1 bg-black/60 backdrop-blur-md text-white text-[10px] font-black tracking-widest rounded-lg">
              {Math.floor(video.durationSeconds / 60)}:{String(video.durationSeconds % 60).padStart(2, '0')}
            </div>
          )}
        </div>
        <div className="p-5 flex-1 flex flex-col gap-3">
          <div>
            <h3 className="text-sm font-bold text-slate-900 dark:text-white leading-snug group-hover:text-primary transition-colors line-clamp-2">
              {video.title}
            </h3>
            <p className="text-[10px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-widest mt-1">
              {new Date(video.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
            </p>
          </div>
          {(dealershipName || uploaderLabel !== '—') && (
            <div className="flex flex-col gap-1">
              {dealershipName && (
                <div className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-400 dark:text-slate-500">
                  <span className="material-symbols-outlined text-[12px]">business</span>
                  {dealershipName}
                </div>
              )}
              {uploaderLabel !== '—' && (
                <div className="flex items-center gap-1.5 text-[10px] font-semibold text-slate-400 dark:text-slate-500">
                  <span className="material-symbols-outlined text-[12px]">person</span>
                  {uploaderLabel}
                </div>
              )}
            </div>
          )}
          <div className="mt-auto pt-3 border-t border-slate-50 dark:border-white/5 flex items-center justify-between">
            {video.videoScore ? (
              <div className="flex items-center gap-3">
                <GradeBadge grade={video.videoScore.grade as Grade} size="sm" />
                <div className="flex flex-col">
                  <span className="text-[14px] font-black text-slate-900 dark:text-white">{video.videoScore.compositeScore}</span>
                  <span className="text-[9px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-tighter -mt-1">VQS Score</span>
                </div>
              </div>
            ) : (
              <ScoringStatusChip status={video.scoringStatus as ScoringStatus} />
            )}
          </div>
        </div>
      </div>
    );
  };

  const VideoRow: React.FC<{ video: Video }> = ({ video }) => {
    const isClickable = video.scoringStatus !== 'PENDING' && video.scoringStatus !== 'PROCESSING';
    const thumbUrl = thumbnailUrls[video.id];
    const dealershipName = tenantMap[video.tenantId] ?? '—';
    const uploaderLabel = uploaderName(video);
    return (
      <tr
        onClick={() => isClickable && navigate(`/videos/${video.id}`)}
        className={`border-b border-slate-50 dark:border-slate-800/50 transition-colors ${isClickable ? 'cursor-pointer hover:bg-slate-50/50 dark:hover:bg-white/[0.02]' : 'opacity-60'}`}
      >
        <td className="px-6 py-4">
          <div className="flex items-center gap-4">
            <div className="w-16 h-10 rounded-lg overflow-hidden bg-slate-100 dark:bg-slate-800 shrink-0">
              {thumbUrl ? (
                <img src={thumbUrl} alt={video.title} className="w-full h-full object-cover" />
              ) : (
                <div className="w-full h-full flex items-center justify-center">
                  <span className="material-symbols-outlined text-[16px] text-slate-300 dark:text-slate-600">videocam</span>
                </div>
              )}
            </div>
            <div>
              <p className="text-sm font-bold text-slate-900 dark:text-white line-clamp-1">{video.title}</p>
              <p className="text-[10px] font-black text-slate-300 dark:text-slate-600 uppercase tracking-widest mt-0.5">
                {new Date(video.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
              </p>
            </div>
          </div>
        </td>
        <td className="px-4 py-4 text-xs text-slate-500 dark:text-slate-400 font-medium">{dealershipName}</td>
        <td className="px-4 py-4 text-xs text-slate-500 dark:text-slate-400 font-medium">{uploaderLabel}</td>
        <td className="px-4 py-4">
          {video.videoScore ? (
            <div className="flex items-center gap-2">
              <GradeBadge grade={video.videoScore.grade as Grade} size="sm" />
              <span className="text-sm font-black text-slate-900 dark:text-white">{video.videoScore.compositeScore}</span>
            </div>
          ) : (
            <ScoringStatusChip status={video.scoringStatus as ScoringStatus} />
          )}
        </td>
        <td className="px-4 py-4 text-xs text-slate-400 dark:text-slate-500 font-medium">
          {video.durationSeconds ? `${Math.floor(video.durationSeconds / 60)}:${String(video.durationSeconds % 60).padStart(2, '0')}` : '—'}
        </td>
      </tr>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50/50 dark:bg-slate-950/50 text-slate-900 dark:text-white transition-colors duration-300 font-sans flex flex-col">
      <TopNav />
      <main className="flex-1 w-full max-w-7xl mx-auto px-6 py-12">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-start justify-between gap-6 mb-8">
          <div>
            <h1 className="text-3xl font-bold text-slate-900 dark:text-white tracking-tight leading-tight">
              Video Library
            </h1>
            <p className="text-slate-500 dark:text-slate-400 font-medium text-base mt-2">
              <span className="text-slate-900 dark:text-white font-bold">{total}</span> videos across group
            </p>
          </div>
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${viewMode === 'grid' ? 'bg-primary text-white' : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-400 hover:text-primary'}`}
            >
              <span className="material-symbols-outlined text-[18px]">grid_view</span>
            </button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              className={`w-9 h-9 rounded-xl flex items-center justify-center transition-all ${viewMode === 'list' ? 'bg-primary text-white' : 'bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 text-slate-400 hover:text-primary'}`}
            >
              <span className="material-symbols-outlined text-[18px]">list</span>
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl p-5 mb-8 space-y-4">
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Account</label>
              <select className={selectClass} style={selectStyle} value={selectedTenantId} onChange={(e) => setSelectedTenantId(e.target.value)} disabled={tenantsLoading}>
                <option value="">All Dealerships</option>
                {tenants.map((t) => (
                  <option key={t.id} value={t.id}>{t.name}</option>
                ))}
              </select>
            </div>

            {selectedTenantId && (
              <div className="flex flex-col gap-1.5">
                <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Sales Rep</label>
                <select className={selectClass} style={selectStyle} value={selectedUserId} onChange={(e) => setSelectedUserId(e.target.value)}>
                  <option value="">All Reps</option>
                  {users.map((u) => (
                    <option key={u.id} value={u.id}>{userName(u)}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Status</label>
              <select className={selectClass} style={selectStyle} value={selectedStatus} onChange={(e) => setSelectedStatus(e.target.value)}>
                {STATUS_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Grade</label>
              <select className={selectClass} style={selectStyle} value={selectedGrade} onChange={(e) => setSelectedGrade(e.target.value)}>
                {GRADE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">Sort</label>
              <select className={selectClass} style={selectStyle} value={selectedSort} onChange={(e) => setSelectedSort(e.target.value)}>
                {SORT_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>
          </div>

          <div className="flex flex-wrap items-end gap-3">
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">From Date</label>
              <input type="date" className={dateClass} value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} max={dateTo || undefined} onClick={(e) => (e.currentTarget as HTMLInputElement).showPicker?.()} />
            </div>
            <div className="flex flex-col gap-1.5">
              <label className="text-[10px] font-black uppercase tracking-widest text-slate-400 dark:text-slate-600">To Date</label>
              <input type="date" className={dateClass} value={dateTo} onChange={(e) => setDateTo(e.target.value)} min={dateFrom || undefined} onClick={(e) => (e.currentTarget as HTMLInputElement).showPicker?.()} />
            </div>
            {hasActiveFilters && (
              <button
                type="button"
                onClick={clearFilters}
                className="h-10 px-4 rounded-xl text-xs font-bold text-slate-500 hover:text-primary border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 hover:border-primary/30 transition-all flex items-center gap-1.5"
              >
                <span className="material-symbols-outlined text-[14px]">close</span>
                Clear Filters
              </button>
            )}
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <div className="w-8 h-8 border-3 border-primary border-t-transparent animate-spin rounded-full shadow-[0_0_10px_rgba(var(--primary-rgb),0.2)]" />
            <p className="text-sm font-bold text-slate-400 dark:text-slate-500 tracking-tight">Syncing library...</p>
          </div>
        ) : videos.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-32 bg-white dark:bg-slate-900/40 border border-slate-100 dark:border-slate-800 border-dashed rounded-3xl text-slate-400 dark:text-slate-600">
            <div className="w-16 h-16 rounded-2xl bg-slate-50 dark:bg-slate-800 flex items-center justify-center mb-6">
              <span className="material-symbols-outlined text-5xl">videocam_off</span>
            </div>
            <p className="font-bold text-lg text-slate-900 dark:text-white tracking-tight">No videos found</p>
            <p className="text-sm font-medium mt-1">Try adjusting the filters above.</p>
          </div>
        ) : viewMode === 'grid' ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {videos.map((video) => <VideoCard key={video.id} video={video} />)}
            </div>
          </>
        ) : (
          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="bg-slate-50/50 dark:bg-slate-800/50 border-b border-slate-100 dark:border-slate-800">
                  <th className="text-left px-6 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Video</th>
                  <th className="text-left px-4 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Dealership</th>
                  <th className="text-left px-4 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Uploader</th>
                  <th className="text-left px-4 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Score</th>
                  <th className="text-left px-4 py-4 text-[10px] font-black uppercase tracking-widest text-slate-400">Duration</th>
                </tr>
              </thead>
              <tbody>
                {videos.map((video) => <VideoRow key={video.id} video={video} />)}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination */}
        {!loading && totalPages > 1 && (
          <div className="flex items-center justify-center gap-4 mt-12 p-2 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl w-fit mx-auto shadow-sm">
            <button
              disabled={page <= 1}
              onClick={() => { setPage(page - 1); void loadVideos(page - 1); }}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-primary hover:bg-primary/5 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
            >
              <span className="material-symbols-outlined">chevron_left</span>
            </button>
            <div className="px-4 text-sm font-bold text-slate-400 dark:text-slate-600 uppercase tracking-widest leading-none flex items-center">
              <span className="text-slate-900 dark:text-white">{page}</span>
              <span className="mx-2 text-slate-200 dark:text-slate-800">/</span>
              {totalPages}
            </div>
            <button
              disabled={page >= totalPages}
              onClick={() => { setPage(page + 1); void loadVideos(page + 1); }}
              className="w-10 h-10 rounded-xl flex items-center justify-center text-slate-400 dark:text-slate-500 hover:text-primary hover:bg-primary/5 disabled:opacity-20 disabled:cursor-not-allowed transition-all"
            >
              <span className="material-symbols-outlined">chevron_right</span>
            </button>
          </div>
        )}
      </main>
    </div>
  );
};

export default GroupAdminVideosPage;
